import React, {useEffect, useState} from "react";
import {Reservation} from "types/entities/Reservation";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {closeModal} from "helpers/actions";
import FeatherIcon from "components/FeatherIcon";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {PackageCreditItemType} from "types/enums/PackageCreditItemType";
import {useTypedSelector} from "helpers/reducers";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import CreditsBalanceDriverItem from "./CreditsBalanceDriverItem";

interface CreditsBalanceModalProps {
    reservation: Reservation | null;
}

type DriversLimits = Record<number, number | null | undefined>;

const CreditsBalanceModal = (props: CreditsBalanceModalProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const sessionUser = useTypedSelector(state => state.bookingLoginState.user);

    const creditItems = props.reservation?.packagesCart?.packagesCartItems?.filter(item => item?.packageItem?.type === PackageItemItemType.Credit);
    const familyCreditItem = creditItems?.find(item => item?.packageItem?.article?.creditPackage?.category === PackageCreditItemType.Family);
    const totalCredits = familyCreditItem?.packageItem?.article?.creditPackage?.amount_total || 0;

    const [effectiveDriversLimits, setEffectiveDriversLimits] = useState<DriversLimits>({});
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        if (!props.reservation?.reservationDrivers) {
            setEffectiveDriversLimits({});
            return;
        }

        const initialLimits: DriversLimits = {};

        props.reservation.reservationDrivers.forEach(reservationDriver => {
            initialLimits[reservationDriver.id] = reservationDriver.credit_limit;
        });

        setEffectiveDriversLimits(initialLimits);
    }, [props.reservation?.id]);

    const handleLimitChange = (rdId: number, newLimit: number | null) => {
        setEffectiveDriversLimits(prev => ({
            ...prev,
            [rdId]: newLimit,
        }));
    };

    const handleRemoveLimit = (rdId: number) => {
        setEffectiveDriversLimits(prev => ({
            ...prev,
            [rdId]: null,
        }));
    };

    const onCloseModalHandler = () => {
        dispatch(closeModal());
    };

    const usedCredits = Object.values(effectiveDriversLimits).reduce<number>((acc, val) => acc + (typeof val === "number" ? val : 0), 0);
    const freeCredits = Math.max(0, totalCredits - usedCredits);
    const driversWithLimit = Object.values(effectiveDriversLimits).filter((limit): limit is number => typeof limit === "number" && limit > 0).length;

    const handleSaveHandler = async () => {
        if (isSaving || !props.reservation) {
            return;
        }

        setIsSaving(true);

        try {
            await dispatch(BookingLoginAction.fetchSetReservationDriverCreditLimits(props.reservation.id, effectiveDriversLimits));
            onCloseModalHandler();
        } catch (error) {
            // Global interceptor handles the error UI. Keep the modal open and draft values intact.
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <CardComponent
            className={
                "CreditsBalanceModal Booking min-w-full max-w-full md:min-w-[700px] lg:min-w-[980px] xl:min-w-[78rem] 2xl:min-w-[80rem] xl:max-w-7xl w-full !p-0 relative overflow-hidden h-auto flex flex-col"
            }
        >
            <div
                className={
                    "top-0 z-30 sticky flex flex-row justify-between rounded-tl-lg rounded-tr-lg w-full px-6 py-4 md:py-8 font-bold border-b-[1px] border-[#EEEEEE] bg-white text-black drop-shadow-sm min-w-full"
                }
            >
                <div className={"flex flex-row items-center gap-4"}>
                    <img src="/img/credits/arbo-credits-icon.svg" alt={t("credits icon")} className="size-[40px] md:size-[60px] lg:size-[90px] flex-none" />
                    <div className={"w-px self-stretch bg-arbo-red hidden md:block"}></div>
                    <div className="flex flex-col">
                        <div className={"font-bold text-lg md:text-2xl"}>{t("Credit limits")}</div>
                        <div className={"font-medium text-sm md:text-lg text-gray-500"}>{t("Optional · can be changed at any time, even in the park")}</div>
                        <div className={"font-bold text-sm md:text-lg"}>
                            <span>{t("Guthaben")}: </span>
                            <span>
                                {totalCredits} {t("Credits")}
                            </span>
                        </div>
                    </div>
                </div>
                <button className={"CreditsBalanceModalClose print:hidden absolute right-4 top-4 z-[100]"} type={"button"} onClick={onCloseModalHandler}>
                    <span
                        className={
                            "bg-white rounded-full size-[40px] md:size-[50px] border-[2px] border-arbo-red flex items-center justify-center hover:bg-gray-50 transition-colors"
                        }
                    >
                        <FeatherIcon name={"x"} className={"text-black size-[18px] md:size-[20px]"} strokeWidth={4} />
                    </span>
                </button>
            </div>

            <div className={"flex w-full flex-col px-6 pt-6 pb-6 bg-[#F9F9F9] overflow-hidden h-auto"}>
                <div className="mb-4 text-xs md:text-sm font-semibold text-black">
                    {t("Gemeinsames Guthaben mit Credit-Limits — Personen mit Limit können höchstens so viel nutzen.")}
                </div>

                <div className={"flex flex-col overflow-y-auto max-h-[60vh] pr-2"}>
                    {props.reservation?.reservationDrivers?.map(rd => {
                        const currentDriverLimit = effectiveDriversLimits[rd.id];
                        const totalAvailableForDriver = totalCredits - usedCredits + (typeof currentDriverLimit === "number" ? currentDriverLimit : 0);

                        return (
                            <CreditsBalanceDriverItem
                                key={rd.id}
                                reservationDriver={rd}
                                limit={currentDriverLimit}
                                onLimitChange={newLimit => handleLimitChange(rd.id, newLimit)}
                                onRemoveLimit={() => handleRemoveLimit(rd.id)}
                                totalAvailable={totalAvailableForDriver}
                                isMainAccount={rd.driver?.id === sessionUser?.id}
                            />
                        );
                    })}
                </div>

                <div className="mt-4 font-bold text-sm text-black">
                    {t("{{count}} mit Limit (zusammen max. {{total}}) · {{free}} frei.", {
                        count: driversWithLimit,
                        total: usedCredits,
                        free: freeCredits,
                    })}
                </div>

                <div className={"flex flex-row items-center justify-between w-full border-t-[1px] border-[#EEEEEE] pt-6 mt-6"}>
                    <p className="text-gray-500 text-xs md:text-sm font-medium">{t("Änderungen sind jederzeit möglich — online oder am Kiosk im Park.")}</p>
                    <button
                        className="bg-white border-2 border-black rounded-xl px-8 py-3 flex flex-row items-center gap-2 hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        type={"button"}
                        disabled={isSaving}
                        onClick={handleSaveHandler}
                    >
                        <span className="font-bold text-sm md:text-base">{t("Speichern")}</span>
                        <FeatherIcon name={"check"} className={"text-black size-[18px] md:size-[20px]"} strokeWidth={3} />
                    </button>
                </div>
            </div>
        </CardComponent>
    );
};

export default CreditsBalanceModal;
